import { InviteFullError } from "@/features/workspace-invites/api/workspace-invites-api";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useSignInWithEmailMutation, useSignInWithOAuthMutation } from "../api/auth-hooks";
import { isKairoEmail } from "../auth.utils";
import {
  clearPostAuthRedirect,
  safeRedirectPath,
  storePostAuthRedirect,
} from "../post-auth-redirect";

/** Errors the form renders with a dedicated test id (copy is "" under vitest). */
export type AuthFormErrorKind = "invite_full";

/**
 * Hook to manage login form logic
 * Handles email/passwordless login, social OAuth login,
 * form state (loading, error, email), and exposes event handlers
 * for UI components to initiate authentication flows.
 */
export function useAuthForm(newUser: boolean) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<AuthFormErrorKind | null>(null);
  const [isSocialRedirecting, setIsSocialRedirecting] = useState(false);

  const signInWithEmailMutation = useSignInWithEmailMutation();
  const signInWithOAuthMutation = useSignInWithOAuthMutation();

  const navigate = useNavigate();

  const { invite_code: inviteCode, redirect } = useSearch({ strict: false }) as {
    [key: string]: string | undefined;
  };
  // CR-288 D2: the page to come back to. The invite flow wins when both are
  // present (its accept page is where the user must land).
  const redirectPath = inviteCode ? undefined : safeRedirectPath(redirect);

  // Also kept in localStorage (30 min) for the post-auth landing, in case the
  // provider or another tab drops the path. Cleared when there is none, so a
  // stale one from an abandoned attempt never hijacks a plain sign-in.
  const rememberRedirect = () => {
    if (redirectPath) {
      storePostAuthRedirect(redirectPath);
    } else {
      clearPostAuthRedirect();
    }
  };

  const handleEmailLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setErrorKind(null);

    try {
      rememberRedirect();
      if (newUser) {
        await signInWithEmailMutation.mutateAsync({
          email,
          inviteCode,
          redirectPath,
        });

        await navigate({
          to: "/signup/check-email",
          search: { email: email, invite_code: undefined, redirect: redirectPath },
        });
      } else {
        await signInWithEmailMutation.mutateAsync({ email, inviteCode, redirectPath });
        await navigate({
          to: "/login/check-email",
          search: { email: email, invite_code: undefined, redirect: redirectPath },
        });
      }
    } catch (err) {
      console.error("Login or sign up failed:", err);

      // CR-185: a domain invite link that reached its maximum.
      if (err instanceof InviteFullError) {
        setErrorKind("invite_full");
        setError(
          "This invite link has reached its maximum number of members. Its owners have been notified; ask them to raise the limit.",
        );
        return;
      }

      const rawMessage =
        err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";

      // Detect a network / CORS / TLS failure — the fetch never reached the server
      const isNetworkError =
        rawMessage === "Failed to fetch" ||
        rawMessage === "Load failed" ||
        rawMessage.toLowerCase().includes("networkerror") ||
        rawMessage.toLowerCase().includes("failed to fetch");

      if (isNetworkError) {
        setError(
          "Could not reach the server. Check your network connection or Supabase URL configuration.",
        );
        return;
      }

      if (newUser) {
        // @kairo.pm addresses should never be told they need an invite link —
        // show the real error so it is actually diagnosable.
        if (isKairoEmail(email)) {
          setError(rawMessage);
        } else {
          setError("You need a valid invite link to create an account.");
        }
      } else {
        setError(
          err instanceof Error ? rawMessage : "An unexpected error occurred. Please try again.",
        );
      }
    }
  };

  const handleSocialLogin = async (provider: "github" | "google") => {
    try {
      setError(null);
      setErrorKind(null);
      setIsSocialRedirecting(true);
      rememberRedirect();
      await signInWithOAuthMutation.mutateAsync({ provider, redirectPath });
    } catch (err) {
      console.error("Social login failed:", err);
      setIsSocialRedirecting(false);
      if (err instanceof Error) {
        setError(`Social login failed: ${err.message}`);
      } else {
        setError("Social login failed. Please try again.");
      }
    }
  };

  const handleGitHubLogin = () => {
    void (async () => {
      try {
        await handleSocialLogin("github");
      } catch (err) {
        console.error(err);
      }
    })();
  };

  const handleGoogleLogin = () => {
    void (async () => {
      try {
        await handleSocialLogin("google");
      } catch (err) {
        console.error(err);
      }
    })();
  };

  return {
    emailLoading: signInWithEmailMutation.isPending,
    anyLoading: signInWithEmailMutation.isPending || isSocialRedirecting,
    error,
    errorKind,
    handleEmailLogin,
    handleGitHubLogin,
    handleGoogleLogin,
    email,
    setEmail,
    inviteCode,
  };
}
